export type Value = boolean | string | number

export type Validator = (value: Value) => boolean

export type ValidationRule = {
  validator: Validator
  error: string
}

export type ValidationRules = Record<string, ValidationRule[]>

export type ValidationErrors = Record<string, string>

export const not = (validator: Validator) => (value: Value) => !validator(value)

export const all = (...validators: Validator[]) => (value: Value) => validators.every((validator) => validator(value))

export const any = (...validators: Validator[]) => (value: Value) => validators.some((validator) => validator(value))

export const length = (validator: Validator) => (value: Value) => {
  if (typeof value === 'boolean') throw Error('Can not get length of boolean value')
  return validator(String(value).length)
}

// eslint-disable-next-line eqeqeq
export const equals = (to: Value) => (value: Value) => value == to

export const notEquals = (value: Value) => not(equals(value))

export const greaterThan = (than: number) => (value: Value) => value > than

export const lessThan = (than: number) => (value: Value) => value < than

export const lengthGreaterThan = (than: number) => length(greaterThan(than))

export const lengthLessThan = (than: number) => length(lessThan(than))

export const inBetween = (min: number, max: number) => all(greaterThan(min), lessThan(max))

export const lengthInBetween = (min: number, max: number) => length(inBetween(min, max))

export const matches = (regexp: RegExp) => (value: Value) => {
  if (typeof value === 'boolean') return false
  return regexp.test(String(value))
}

export const isEmail = matches(/^\w+[\w-\.]*\@\w+((-\w+)|(\w*))\.[a-z]{2,3}$/)

/**
 * Только кирилица и тире
 */
export const cyrillic = matches(/^[а-яё]+(?:[ -]{1}[а-яё]*)?$/i)

export const includes = (needle: string) => (value: Value) => {
  if (typeof value === 'boolean') return false
  return String(value).includes(needle)
}

const notIncludes = (needle: string) => not(includes(needle))

export const isPhone = notIncludes('_')

export const notEmpty = length(notEquals(0))

export const validate = (state: Record<string, Value>, rules: ValidationRules): ValidationErrors => {
  const errors: ValidationErrors = {}
  for (const fieldName in state) {
    const fieldValue = state[fieldName]
    const validators = rules[fieldName]
    if (validators) {
      const fieldError = validators.find((validator) => !validator.validator(fieldValue))

      if (fieldError) {
        errors[fieldName] = fieldError.error
      }
    }
  }

  return errors
}
